/* eslint-disable no-comments/disallowComments */
import React from 'react';
import { styled } from '@mui/material';

type RowGap = 'none' | 'sm' | 'md' | 'lg';
type RowAlign = 'start' | 'center' | 'end' | 'baseline' | 'stretch';
type RowJustify = 'start' | 'center' | 'end' | 'between' | 'around';
type RowComponent = 'div' | 'section' | 'article' | 'aside' | 'header' | 'footer' | 'nav';

interface RowProps {
    children: React.ReactNode;
    /** Gap between children: `none` (0), `sm` (8px), `md` (16px, default), or `lg` (24px). */
    gap?: RowGap;
    /** Cross-axis alignment (vertical). Defaults to `center`. */
    align?: RowAlign;
    /** Main-axis alignment (horizontal). Defaults to `start`. */
    justify?: RowJustify;
    /** If true, allows children to wrap onto multiple lines. */
    wrap?: boolean;
    /** If true, stretches to 100% of parent width. */
    fullWidth?: boolean;
    /** HTML element to render. Defaults to `div`. */
    component?: RowComponent;
    className?: string;
}

const GAP_PX: Record<RowGap, number> = { none: 0, sm: 8, md: 16, lg: 24 };
const ALIGN_VALUE: Record<RowAlign, string> = {
    start: 'flex-start',
    center: 'center',
    end: 'flex-end',
    baseline: 'baseline',
    stretch: 'stretch',
};
const JUSTIFY_VALUE: Record<RowJustify, string> = {
    start: 'flex-start',
    center: 'center',
    end: 'flex-end',
    between: 'space-between',
    around: 'space-around',
};

type RowStyleProps = {
    $gap: RowGap;
    $align: RowAlign;
    $justify: RowJustify;
    $wrap: boolean;
    $fullWidth: boolean;
};

const rowStyles = ({ $gap, $align, $justify, $wrap, $fullWidth }: RowStyleProps) => ({
    display: 'flex',
    flexDirection: 'row' as const,
    gap: GAP_PX[$gap],
    alignItems: ALIGN_VALUE[$align],
    justifyContent: JUSTIFY_VALUE[$justify],
    flexWrap: ($wrap ? 'wrap' : 'nowrap') as 'wrap' | 'nowrap',
    width: $fullWidth ? '100%' : undefined,
});

const shouldForwardRowProp = (prop: string) => !['$gap', '$align', '$justify', '$wrap', '$fullWidth'].includes(prop);

const DivRow = styled('div', { shouldForwardProp: (p) => shouldForwardRowProp(p as string) })<RowStyleProps>(rowStyles);
const SectionRow = styled('section', { shouldForwardProp: (p) => shouldForwardRowProp(p as string) })<RowStyleProps>(
    rowStyles,
);
const ArticleRow = styled('article', { shouldForwardProp: (p) => shouldForwardRowProp(p as string) })<RowStyleProps>(
    rowStyles,
);
const AsideRow = styled('aside', { shouldForwardProp: (p) => shouldForwardRowProp(p as string) })<RowStyleProps>(
    rowStyles,
);
const HeaderRow = styled('header', { shouldForwardProp: (p) => shouldForwardRowProp(p as string) })<RowStyleProps>(
    rowStyles,
);
const FooterRow = styled('footer', { shouldForwardProp: (p) => shouldForwardRowProp(p as string) })<RowStyleProps>(
    rowStyles,
);
const NavRow = styled('nav', { shouldForwardProp: (p) => shouldForwardRowProp(p as string) })<RowStyleProps>(rowStyles);

const COMPONENTS = {
    div: DivRow,
    section: SectionRow,
    article: ArticleRow,
    aside: AsideRow,
    header: HeaderRow,
    footer: FooterRow,
    nav: NavRow,
};

export function Row({
    children,
    gap = 'md',
    align = 'center',
    justify = 'start',
    wrap = false,
    fullWidth = false,
    component = 'div',
    className,
}: RowProps) {
    const Component = COMPONENTS[component];
    return (
        <Component
            $gap={gap}
            $align={align}
            $justify={justify}
            $wrap={wrap}
            $fullWidth={fullWidth}
            className={className}
        >
            {children}
        </Component>
    );
}
/* eslint-enable no-comments/disallowComments */
